import { VehicleIcon, MotorcycleIcon, TukTukIcon, DeliveryIcon, LocationIcon, WalletIcon, StarIcon, AnalyticsIcon, BookingsIcon, OrdersIcon, WarningIcon, LockedIcon } from "../../lib/cccIcons"
import { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { supabase } from "../../lib/supabase"
import { getCurrentPosition } from "../../lib/geolocation"
import { useAuth } from "../../contexts/AuthContext"
import useIsMobile from "../../lib/useIsMobile"
import toast from "react-hot-toast"
import DriverOnboarding from "./DriverOnboarding"

const VEHICLE_CONFIG = {
  car:        { icon:"vehicle", label:"Car Driver",       color:"#1d9e75", desc:"Standard delivery" },
  motorcycle: { icon:"motorcycle", label:"Boda Boda Rider",   color:"#e6821e", desc:"Fast parts and accessories delivery" },
  tuktuk:     { icon:"tuktuk", label:"Tuktuk Driver",      color:"#378add", desc:"Local area parts delivery" },
  van:        { icon:"delivery", label:"Van Driver",          color:"#8b5cf6", desc:"Bulk parts and large item delivery" },
}

export default function DriverOverview() {
  const { user, profile, profileReady, loading: authLoading } = useAuth()
  console.log("DriverOverview render", { profile, vehicleType: profile?.driver_vehicle_type })
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  useEffect(() => {
    if(profile && !profile.onboarding_complete) setShowOnboarding(true)
    supabase.from("app_settings").select("value").eq("key","concierge_surcharge_rate").maybeSingle().then(({data}) => { if (data) setCommissionRate(Number(data.value)) })
    supabase.from("app_settings").select("value").eq("key","driver_transport_allowance").maybeSingle().then(({data}) => { if (data) setTransportAllowanceAmt(Number(data.value)) })
  }, [profile])
  const [driverStatus, setDriverStatus] = useState(null)
  const [showOnboarding, setShowOnboarding] = useState(false)
  const [commissionRate, setCommissionRate] = useState(15)
  const [transportAllowanceAmt, setTransportAllowanceAmt] = useState(200)
  const [loading, setLoading] = useState(true)
  const [toggling, setToggling] = useState(false)
  const [activeJob, setActiveJob] = useState(null)
  const [todayStats, setTodayStats] = useState({ jobs:0, earnings:0, rating:"—" })
  const [weekStats, setWeekStats] = useState({ jobs:0, earnings:0 })
  const [penalties, setPenalties] = useState([])
  const [probation, setProbation] = useState(null)
  const [rating, setRating] = useState(null)
  const locationIntervalRef = useRef(null)

  useEffect(() => {
    if (!user || !profile || !profileReady) return
    load()
    const sub = supabase.channel("driver-overview-live")
      .on("postgres_changes", { event:"*", schema:"public", table:"driver_status", filter:`driver_id=eq.${user.id}` }, () => loadStatus())
      .on("postgres_changes", { event:"*", schema:"public", table:"bookings", filter:`driver_id=eq.${user.id}` }, () => loadActiveJob())
      .subscribe()
    return () => { supabase.removeChannel(sub); if (locationIntervalRef.current) clearInterval(locationIntervalRef.current) }
  }, [user, profileReady])

  async function load() {
    await Promise.all([loadStatus(), loadActiveJob(), loadStats(), loadPenalties(), loadProbation(), loadRating()])
    setLoading(false)
  }

  async function loadStatus() {
    const { data } = await supabase.from("driver_status").select("*").eq("driver_id", user.id).maybeSingle()
    setDriverStatus(data)
    if (data?.is_online) startLocationSharing()
  }

  async function loadActiveJob() {
    const { data } = await supabase.from("bookings").select("*, vehicles(make,model,license_plate)").eq("driver_id", user.id).not("status","in","(completed,cancelled)").maybeSingle()
    setActiveJob(data)
  }

  async function loadStats() {
    const today = new Date().toISOString().split("T")[0]
    const weekAgo = new Date(Date.now()-7*24*60*60*1000).toISOString().split("T")[0]
    const { data: allJobs } = await supabase.from("bookings").select("booking_date,driver_earnings,status").eq("driver_id", user.id).eq("status","completed")
    const todayJobs = (allJobs||[]).filter(j=>j.booking_date===today)
    const weekJobs = (allJobs||[]).filter(j=>j.booking_date>=weekAgo)
    setTodayStats({ jobs:todayJobs.length, earnings:todayJobs.reduce((s,j)=>s+Number(j.driver_earnings||0),0), rating:"—" })
    setWeekStats({ jobs:weekJobs.length, earnings:weekJobs.reduce((s,j)=>s+Number(j.driver_earnings||0),0) })
  }

  async function loadPenalties() {
    const { data } = await supabase.from("driver_penalties").select("*").eq("driver_id", user.id).eq("is_active", true)
    setPenalties(data||[])
  }

  async function loadProbation() {
    const { data } = await supabase.from("driver_probation").select("*").eq("driver_id", user.id).maybeSingle()
    setProbation(data)
  }

  async function loadRating() {
    const { data } = await supabase.from("reviews").select("provider_rating").eq("driver_id", user.id)
    if (data?.length) {
      const avg = data.reduce((s,r)=>s+Number(r.provider_rating||0),0)/data.length
      setRating(avg.toFixed(1))
    }
  }

  async function updateLocation(lat, lng) {
    try {
      await supabase.from("driver_status").upsert({ driver_id:user.id, current_lat:lat, current_lng:lng, last_location_update:new Date().toISOString(), updated_at:new Date().toISOString() }, { onConflict:"driver_id" })
      await supabase.from("profiles").update({ latitude:lat, longitude:lng }).eq("id", user.id)
    } catch(err) { console.error("Location update failed:", err) }
  }

  function startLocationSharing() {
    if (locationIntervalRef.current) clearInterval(locationIntervalRef.current)
    locationIntervalRef.current = setInterval(() => {
      getCurrentPosition().then(async pos => {
        // Always update driver_status
        await supabase.from("driver_status").update({
          current_lat:pos.latitude, current_lng:pos.longitude, updated_at:new Date().toISOString()
        }).eq("driver_id", user.id)
        // Log to booking_location_logs if active booking
        const { data: activeBooking } = await supabase.from("bookings")
          .select("id").eq("driver_id", user.id)
          .in("status", ["confirmed","in-progress","driver-assigned"])
          .maybeSingle()
        if (activeBooking?.id) {
          await supabase.from("booking_location_logs").insert({
            booking_id: activeBooking.id, driver_id: user.id,
            lat: pos.latitude, lng: pos.longitude, source: "driver"
          })
        }
        // Log for active order deliveries
        const { data: activeOrder } = await supabase.from("orders")
          .select("id").eq("delivery_driver_id", user.id)
          .in("delivery_status", ["assigned","picked_up"])
          .maybeSingle()
        if (activeOrder?.id) {
          await supabase.from("booking_location_logs").insert({
            booking_id: activeOrder.id, driver_id: user.id,
            lat: pos.latitude, lng: pos.longitude, source: "driver"
          })
        }
      }).catch(err => console.warn("Location update failed:", err.message))
    }, 30000)
  }

  async function toggleOnline() {
    if (!profile?.documents_verified) return toast.error("Your documents must be verified before going online")
    if (driverStatus?.is_suspended) {
      const expires = driverStatus.suspension_expires_at ? new Date(driverStatus.suspension_expires_at).toLocaleString() : "permanently"
      return toast.error(`You are suspended until ${expires}`)
    }
    setToggling(true)
    try {
      const newStatus = !driverStatus?.is_online
      await supabase.from("driver_status").upsert({ driver_id:user.id, is_online:newStatus, updated_at:new Date().toISOString() }, { onConflict:"driver_id" })
      setDriverStatus(s=>({...s, is_online:newStatus}))
      if (newStatus) startLocationSharing()
      else if (locationIntervalRef.current) clearInterval(locationIntervalRef.current)
      toast.success(newStatus?"You are now ONLINE 🟢":"You are now OFFLINE 🔴")
    } catch(err) { toast.error(err.message) }
    finally { setToggling(false) }
  }

  const isOnline = driverStatus?.is_online
  const isVerified = profile?.documents_verified
  const isSuspended = driverStatus?.is_suspended
  const noShowCount = driverStatus?.no_show_count||0
  const vehicleType = profile?.driver_vehicle_type || "car"
  const isConcierge = profile?.driver_category === "concierge"
  const vehicleConfig = isConcierge 
    ? { icon:"🧑‍✈️", label:"Concierge Driver", color:"#1d9e75", desc:"Vehicle pickup and chauffeur service" }
    : (VEHICLE_CONFIG[vehicleType] || VEHICLE_CONFIG.car)

  if (loading || authLoading || !profileReady) return <div style={{ color:"#888", fontSize:13, padding:"2rem", textAlign:"center" }}>Loading...</div>

  return (
    <div style={{ margin:"-1rem", fontFamily:"DM Sans,sans-serif" }}>
      {/* Style 1 header */}
      <div style={{ background:isSuspended?"#e24b4a":isOnline?vehicleConfig.color:"#000", padding:"1.25rem 1.25rem 2.5rem", transition:"background 0.3s" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
          <div>
            <div style={{ fontSize:11, color:"rgba(255,255,255,0.7)", marginBottom:2 }}>
              {new Date().toLocaleDateString("en-KE",{weekday:"long",day:"numeric",month:"long"})}
            </div>
            <div style={{ fontFamily:"Syne", fontSize:18, fontWeight:800, color:"#fff" }}>
              {profile?.first_name} {profile?.last_name}
            </div>
            <div style={{ fontSize:11, color:"rgba(255,255,255,0.8)", marginTop:2 }}>
              {vehicleConfig.icon} {vehicleConfig.label}
              {profile?.is_verified&&<span style={{ marginLeft:8, background:"rgba(255,255,255,0.2)", borderRadius:10, padding:"1px 8px", fontSize:10 }}>✓ Verified</span>}
            </div>
          </div>
          <button onClick={toggleOnline} disabled={toggling||!isVerified||isSuspended}
            style={{ background:isOnline?"rgba(255,255,255,0.2)":"#e6821e", border:"none", borderRadius:20, color:"#fff", fontSize:12, fontWeight:700, padding:"8px 16px", cursor:!isVerified||toggling||isSuspended?"not-allowed":"pointer", fontFamily:"Syne,sans-serif", opacity:!isVerified||isSuspended?0.6:1 }}>
            {toggling?"...":(isSuspended?"SUSPENDED":isOnline?"● Go Offline":"○ Go Online")}
          </button>
        </div>
        {isOnline&&(
          <div style={{ marginTop:8, fontSize:10, color:"rgba(255,255,255,0.7)", display:"flex", alignItems:"center", gap:6 }}>
            <div style={{ width:5, height:5, borderRadius:"50%", background:"#fff" }}/>
            Location sharing active · Receiving job requests
          </div>
        )}
      </div>

      {/* Floating stats card */}
      <div style={{ margin:"-1.25rem 1rem 1rem", background:"#fff", borderRadius:16, padding:"1rem", boxShadow:"0 4px 20px rgba(0,0,0,0.08)", border:"1px solid #f0f0f0" }}>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:8 }}>
          {[
            { label:"Today jobs", value:todayStats.jobs, icon:"📅", color:"#378add" },
            { label:"Today earnings", value:`KES ${Number(todayStats.earnings).toLocaleString()}`, icon:"wallet", color:"#1d9e75" },
            { label:"Week jobs", value:weekStats.jobs, icon:"analytics", color:"#8b5cf6" },
            { label:"Rating", value:rating?`⭐ ${rating}`:"—", icon:"star", color:"#e6821e" },
          ].map(s=>(
            <div key={s.label} style={{ background:"#f8f8f8", borderRadius:10, padding:"0.75rem", display:"flex", alignItems:"center", gap:8 }}>
              <div style={{display:"flex",alignItems:"center"}}>{s.icon==="wallet"?<WalletIcon size={20} color={s.color}/>:s.icon==="analytics"?<AnalyticsIcon size={20} color={s.color}/>:s.icon==="star"?<StarIcon size={20} color={s.color}/>:<VehicleIcon size={20} color={s.color}/>}</div>
              <div>
                <div style={{ fontFamily:"Syne", fontSize:15, fontWeight:800, color:s.color }}>{s.value}</div>
                <div style={{ fontSize:10, color:"#888" }}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ padding:"0 1rem" }}>
        {/* Suspension banner */}
        {isSuspended&&(
          <div style={{ background:"#fff5f5", border:"2px solid #e24b4a", borderRadius:12, padding:"1rem", marginBottom:"1rem" }}>
            <div style={{ fontFamily:"Syne", fontSize:14, fontWeight:800, color:"#e24b4a", marginBottom:4 }}>🚫 Account suspended</div>
            <div style={{ fontSize:12, color:"#555", marginBottom:4 }}>
              {driverStatus?.suspension_expires_at ? `Suspended until: ${new Date(driverStatus.suspension_expires_at).toLocaleString()}` : "You have been permanently suspended."}
            </div>
            <div style={{ fontSize:11, color:"#888" }}>Contact support if you believe this is an error.</div>
          </div>
        )}

        {/* No-show warning */}
        {noShowCount>0&&!isSuspended&&(
          <div style={{ background:"#fff8f0", border:"1px solid #e6821e40", borderRadius:10, padding:"0.85rem", marginBottom:"1rem" }}>
            <div style={{ fontSize:13, color:"#e6821e", fontWeight:600, marginBottom:2, display:"flex", alignItems:"center", gap:4 }}><WarningIcon size={13} color="#e6821e"/> No-show warning</div>
            <div style={{ fontSize:11, color:"#666" }}>
              You have {noShowCount} no-show{noShowCount>1?"s":""} recorded.
              {noShowCount===1&&" One more will result in a 72hr suspension."}
              {noShowCount>=2&&" Next no-show will result in permanent ban."}
            </div>
          </div>
        )}

        {/* Docs not verified */}
        {!isVerified&&!isSuspended&&(
          <div style={{ background:"#fff8f0", border:"1px solid #e6821e40", borderRadius:10, padding:"0.85rem", marginBottom:"1rem" }}>
            <div style={{ fontSize:12, color:"#e6821e", display:"flex", alignItems:"center", gap:4 }}><WarningIcon size={12} color="#e6821e"/> Documents pending verification — go to Profile → Credentials</div>
          </div>
        )}

        {/* Probation status */}
        {probation&&probation.status==="probation"&&(
          <div style={{ background:"#faf5ff", border:"1px solid #8b5cf640", borderRadius:12, padding:"1rem", marginBottom:"1rem" }}>
            <div style={{ fontFamily:"Syne", fontSize:13, fontWeight:700, color:"#8b5cf6", marginBottom:6 }}>🔍 Probation period</div>
            <div style={{ fontSize:12, color:"#555", marginBottom:8 }}>Complete {probation.jobs_required} jobs with 4.5+ stars to become a fully verified driver.</div>
            <div style={{ background:"#ede9fe", borderRadius:8, height:8, overflow:"hidden" }}>
              <div style={{ background:"#8b5cf6", height:"100%", width:`${Math.min(100,(probation.jobs_completed/probation.jobs_required)*100)}%`, borderRadius:8, transition:"width 0.3s" }}/>
            </div>
            <div style={{ fontSize:11, color:"#8b5cf6", marginTop:4, textAlign:"right" }}>{probation.jobs_completed}/{probation.jobs_required} jobs completed</div>
          </div>
        )}
        {probation&&probation.status==="completed"&&(
          <div style={{ background:"#f0fdf4", border:"1px solid #1d9e7540", borderRadius:10, padding:"0.85rem", marginBottom:"1rem" }}>
            <div style={{ fontSize:13, color:"#1d9e75", fontWeight:600 }}>✓ Probation completed — you are a fully verified CCC Driver!</div>
          </div>
        )}

        {/* Quick actions */}
        {isOnline&&isVerified&&!isSuspended&&(
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:"1rem" }}>
            <button onClick={()=>navigate(isConcierge?"/dashboard/jobs":"/dashboard/deliveries")} style={{ background:"#1d9e75", border:"none", borderRadius:10, padding:"0.85rem", textAlign:"center", cursor:"pointer", width:"100%" }}>
              <div style={{ marginBottom:2, display:"flex", justifyContent:"center" }}>{isConcierge?<OrdersIcon size={20} color="#fff"/>:<DeliveryIcon size={20} color="#fff"/>}</div>
              <div style={{ fontFamily:"Syne", fontSize:12, fontWeight:700, color:"#fff" }}>{isConcierge?"Available Jobs":"Order Deliveries"}</div>
            </button>
            <button onClick={()=>navigate(isConcierge?"/dashboard/active":"/dashboard/deliveries")} style={{ background:"#378add", border:"none", borderRadius:10, padding:"0.85rem", textAlign:"center", cursor:"pointer", width:"100%" }}>
              <div style={{ marginBottom:2, display:"flex", justifyContent:"center" }}>{isConcierge?<VehicleIcon size={20} color="#fff"/>:<OrdersIcon size={20} color="#fff"/>}</div>
              <div style={{ fontFamily:"Syne", fontSize:12, fontWeight:700, color:"#fff" }}>{isConcierge?"Active Delivery":"My Deliveries"}</div>
            </button>
          </div>
        )}

        {/* Active job */}
        {activeJob&&(
          <div style={{ background:"#eff6ff", border:"1px solid #378add40", borderRadius:12, padding:"1rem", marginBottom:"1rem" }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
              <div style={{ width:8, height:8, borderRadius:"50%", background:"#378add" }}/>
              <div style={{ fontFamily:"Syne", fontSize:13, fontWeight:700, color:"#378add" }}>Active job</div>
            </div>
            <div style={{ fontSize:13, color:"#000", marginBottom:4 }}>{activeJob.service_name}</div>
            {activeJob.vehicles&&<div style={{ fontSize:11, color:"#888" }}>🚗 {activeJob.vehicles.make} {activeJob.vehicles.model} — {activeJob.vehicles.license_plate}</div>}
            <div style={{ fontSize:11, color:"#888", marginTop:2 }}>Status: {activeJob.concierge_status?.replace("_"," ")||activeJob.status}</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginTop:8 }}>
              <div style={{ background:"#fff", borderRadius:8, padding:"0.6rem", textAlign:"center" }}>
                <div style={{ fontSize:10, color:"#888" }}>Earnings</div>
                <div style={{ fontSize:13, color:"#1d9e75", fontWeight:700 }}>KES {Number(activeJob.driver_earnings||0).toFixed(0)}</div>
              </div>
              <div style={{ background:"#fff", borderRadius:8, padding:"0.6rem", textAlign:"center" }}>
                <div style={{ fontSize:10, color:"#888" }}>Allowance</div>
                <div style={{ fontSize:13, color:"#e6821e", fontWeight:700 }}>KES {Number(activeJob.transport_allowance||transportAllowanceAmt).toLocaleString()}</div>
              </div>
            </div>
            <div style={{ fontSize:11, color:"#378add", marginTop:8, textAlign:"center" }}>Go to Active Delivery to continue →</div>
          </div>
        )}

        {/* Earnings structure */}
        <div style={{ background:"#f8f8f8", borderRadius:12, padding:"1rem", marginBottom:"1rem" }}>
          <div style={{ fontFamily:"Syne", fontSize:13, fontWeight:700, color:"#1d9e75", marginBottom:10, display:"flex", alignItems:"center", gap:6 }}><WalletIcon size={14} color="#1d9e75"/> Your earnings structure</div>
          {(isConcierge ? [
            { icon:"wallet", label:"Commission", desc:`${commissionRate}% of service fee — paid after delivery complete` },
            { icon:"🚌", label:"Transport allowance", desc:`KES ${transportAllowanceAmt} per booking — covers your travel to pick up and return the car` },
            { icon:"warning", label:"No-show policy", desc:"Allowance only paid after pickup report filed. No-shows result in penalties." },
            { icon:"locked", label:"Payment security", desc:"Both released only after you complete the delivery and file the dropoff report" },
          ] : [
            { icon:"orders", label:"Delivery earnings", desc:"85% of delivery fee — paid after successful delivery" },
            { icon:"location", label:"Per delivery", desc:"Earn more by completing more deliveries in your zone" },
            { icon:"warning", label:"No-show policy", desc:"Declining or missing deliveries may result in suspension" },
            { icon:"locked", label:"Payment security", desc:"Earnings released only after customer confirms delivery received" },
          ]).map(item=>(
            <div key={item.label} style={{ display:"flex", gap:10, alignItems:"flex-start", marginBottom:8 }}>
              <span style={{flexShrink:0,display:"flex",alignItems:"center"}}>{item.icon==="orders"?<OrdersIcon size={16} color="#e6821e"/>:item.icon==="location"?<LocationIcon size={16} color="#8b5cf6"/>:item.icon==="warning"?<WarningIcon size={16} color="#e6821e"/>:item.icon==="locked"?<LockedIcon size={16} color="#1d9e75"/>:<WalletIcon size={16} color="#1d9e75"/>}</span>
              <div>
                <div style={{ fontSize:12, color:"#000", fontWeight:600 }}>{item.label}</div>
                <div style={{ fontSize:11, color:"#888", lineHeight:1.4 }}>{item.desc}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Active penalties */}
        {penalties.length>0&&(
          <div style={{ background:"#fff5f5", border:"1px solid #e24b4a30", borderRadius:12, padding:"1rem" }}>
            <div style={{ fontFamily:"Syne", fontSize:13, fontWeight:700, color:"#e24b4a", marginBottom:8 }}>Active penalties</div>
            {penalties.map(p=>(
              <div key={p.id} style={{ display:"flex", justifyContent:"space-between", padding:"6px 0", borderBottom:"0.5px solid #fee2e2", fontSize:12 }}>
                <span style={{ color:"#e24b4a" }}>{p.penalty_type?.replace("_"," ")}</span>
                <span style={{ color:"#888" }}>{p.reason}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      {showOnboarding && <DriverOnboarding onComplete={()=>{ setShowOnboarding(false); window.location.reload() }} />}
    </div>
  )
}














